import React from "react";

/* Small shared pieces of the Billing module, in care's current visual language (Tailwind, navy #1D2951). */

export const STATUS_LABELS = {
  draft: "Draft",
  ready: "Ready",
  generated: "In 837 file",
  submitted_athena: "Sent to Athena",
  submitted_clearinghouse: "Sent to clearinghouse",
};

const STATUS_STYLE = {
  draft: "bg-gray-100 text-gray-700",
  ready: "bg-blue-50 text-blue-700",
  generated: "bg-indigo-50 text-indigo-700",
  submitted_athena: "bg-green-100 text-green-700",
  submitted_clearinghouse: "bg-green-100 text-green-700",
};

export const Pill = ({ className = "", children, title }) => (
  <span title={title} className={`inline-block text-[11px] leading-4 px-2 py-0.5 rounded-full whitespace-nowrap ${className}`}>
    {children}
  </span>
);

export const StatusPill = ({ status }) =>
  status ? <Pill className={STATUS_STYLE[status] || "bg-gray-100 text-gray-700"}>{STATUS_LABELS[status] || status}</Pill>
         : <span className="text-[12px] text-[#9CA3AF]">No claim</span>;

export const DummyPill = ({ show = true }) =>
  show ? <Pill className="bg-amber-100 text-amber-800" title="Placeholder value until Office Ally sends the real one">DUMMY</Pill> : null;

export const Button = ({ kind = "primary", className = "", ...props }) => {
  const look = {
    primary: "bg-[#1D2951] text-white hover:bg-[#2a3a6e] disabled:bg-[#9CA3AF]",
    outline: "border border-[#1D2951] text-[#1D2951] bg-white hover:bg-[#EEF2FB] disabled:text-[#9CA3AF] disabled:border-[#D1D5DB]",
    ghost: "text-[#1D2951] hover:bg-[#EEF2FB] disabled:text-[#9CA3AF]",
    danger: "border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:text-[#9CA3AF]",
  }[kind];
  return (
    <button type="button" className={`px-3 py-1.5 rounded-lg text-sm font-semibold disabled:cursor-not-allowed ${look} ${className}`}
      {...props} />
  );
};

export const Card = ({ title, right, children, className = "" }) => (
  <section className={`bg-white rounded-xl border border-[#E5E7EB] ${className}`}>
    {(title || right) && (
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-[#E5E7EB]">
        <h3 className="text-[13px] font-semibold text-[#18181B]">{title}</h3>
        <div className="flex items-center gap-2">{right}</div>
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

export const Field = ({ label, error, hint, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[11px] font-medium text-[#6B7280] mb-1">{label}</span>
    {children}
    {error ? <span className="block text-[11px] text-red-700 mt-0.5">{error}</span>
           : hint ? <span className="block text-[11px] text-[#9CA3AF] mt-0.5">{hint}</span> : null}
  </label>
);

export const inputCls = (bad) =>
  `w-full border rounded-md px-2 py-1 text-[13px] text-[#18181B] bg-white focus:outline-none focus:ring-1 focus:ring-[#1D2951] ${
    bad ? "border-red-400" : "border-[#D1D5DB]"}`;

export const TextInput = ({ value, onChange, bad, className = "", ...props }) => (
  <input className={`${inputCls(bad)} ${className}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...props} />
);

// Errors / warnings / notes from the server's validators ({code, field, message}).
export const Messages = ({ errors = [], warnings = [], notes = [], compact = false }) => {
  if (!errors.length && !warnings.length && !notes.length) {
    return <div className="text-[12px] text-green-700">No problems found.</div>;
  }
  const row = (m, i, cls, label) => (
    <li key={`${label}-${i}`} className={`flex gap-2 text-[12px] ${cls}`}>
      <span className="font-semibold shrink-0 w-[62px]">{label}</span>
      <span>
        {m.message}
        {m.field && !compact ? <span className="text-[#9CA3AF]"> ({m.field})</span> : null}
        {m.appointmentid ? <span className="text-[#9CA3AF]"> visit {m.appointmentid}</span> : null}
      </span>
    </li>
  );
  return (
    <ul className="space-y-1">
      {errors.map((m, i) => row(m, i, "text-red-700", "Error"))}
      {warnings.map((m, i) => row(m, i, "text-amber-700", "Warning"))}
      {notes.map((m, i) => row(m, i, "text-[#6B7280]", "Note"))}
    </ul>
  );
};

// Immutable set at a dotted path ("billing_provider.address.zip"); numeric parts index arrays.
export function setIn(obj, path, value) {
  const parts = Array.isArray(path) ? path : String(path).split(".");
  const [head, ...rest] = parts;
  const base = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  base[head] = rest.length ? setIn(obj ? obj[head] : undefined, rest, value) : value;
  return base;
}

export function getIn(obj, path) {
  return String(path).split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

export const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "";
};
